import '../../i18n/autoTranslate';
import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import GlareHover from '../../../components/ui/shadcn/GlareHover';

const ResourceCard = ({ 
  resource, 
  onBookmark, 
  onPreview, 
  onStartPractice,
  className = "" 
}) => {
  const [isBookmarked, setIsBookmarked] = useState(resource?.isBookmarked || false);

  const handleBookmark = () => {
    setIsBookmarked(!isBookmarked);
    if (onBookmark) {
      onBookmark(resource?.id, !isBookmarked);
    }
  };

  const getDifficultyColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'beginner':
        return 'text-success bg-success/10 border-success/20';
      case 'intermediate':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'advanced':
        return 'text-error bg-error/10 border-error/20';
      default:
        return 'text-muted-foreground bg-muted/10 border-muted/20';
    }
  };

  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'question bank':
        return 'MessageSquare';
      case 'answer template':
        return 'FileText';
      case 'industry guide':
        return 'Building';
      case 'skill exercise':
        return 'Dumbbell';
      case 'video tutorial':
        return 'Play';
      case 'audio practice':
        return 'Headphones';
      case 'mock pack':
        return 'Layers';
      case 'company kit':
        return 'Building2';
      case 'learning path':
        return 'GraduationCap';
      case 'doc guide':
        return 'FileText';
      default:
        return 'BookOpen';
    }
  };

  return (
    <GlareHover
      width="100%"
      height="100%"
      borderRadius="24px"
      borderColor="rgba(255,255,255,0.10)"
      background="rgba(255,255,255,0.04)"
      glareColor="#6C63FF"
      glareOpacity={0.22}
      glareSize={140}
      className={className}
    >
      <div className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/5">
        <div className="relative h-44 overflow-hidden">
          <Image
            src={resource?.thumbnail}
            alt={resource?.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.06]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B12]/90 via-[#0B0B12]/20 to-transparent" />

          <div className="absolute left-4 top-4 flex items-center gap-2">
            {resource?.isNew && (
              <span className="px-2.5 py-1 text-[11px] font-semibold rounded-full bg-white/10 border border-white/10 text-white">
                New
              </span>
            )}
            {resource?.isPremium && (
              <span className="px-2.5 py-1 text-[11px] font-semibold rounded-full bg-gradient-to-r from-[#8B7CFF] to-[#6C63FF] text-white">
                Premium
              </span>
            )}
          </div>

          <div className="absolute right-4 top-4 flex items-center gap-2">
            <button
              onClick={handleBookmark}
              className={`w-9 h-9 rounded-2xl border transition-all duration-200 flex items-center justify-center ${
                isBookmarked
                  ? "bg-white text-black border-white/20"
                  : "bg-black/30 text-white border-white/10 hover:bg-white hover:text-black"
              }`}
            >
              <Icon name="Star" size={16} fill={isBookmarked ? "currentColor" : "none"} />
            </button>

            {resource?.isDownloadable && (
              <button className="w-9 h-9 rounded-2xl bg-black/30 text-white border border-white/10 hover:bg-white hover:text-black transition-all duration-200 flex items-center justify-center">
                <Icon name="Download" size={16} />
              </button>
            )}
          </div>

          <div className="absolute bottom-3 left-3 right-3 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
            <Button
              variant="default"
              size="sm"
              onClick={() => onPreview && onPreview(resource)}
              className="w-full"
              iconName="Eye"
              iconPosition="left"
            >
              Preview
            </Button>
          </div>
        </div>

        <div className="p-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-xs text-white/60">
                <Icon name={getTypeIcon(resource?.type)} size={14} className="text-white/70" />
                <span className="uppercase tracking-wide">{resource?.type}</span>
              </div>
              <h3 className="mt-2 text-lg font-semibold text-white line-clamp-2">
                {resource?.title}
              </h3>
            </div>

            <div className={`shrink-0 px-2.5 py-1 text-xs font-semibold rounded-full border ${getDifficultyColor(resource?.difficulty)}`}>
              {resource?.difficulty}
            </div>
          </div>

          <p className="text-sm text-white/65 mb-4 line-clamp-2">
            {resource?.description}
          </p>

          {(resource?.role || resource?.interviewType || resource?.youtubeId) && (
            <div className="flex flex-wrap gap-2 mb-4">
              {resource?.role && (
                <span className="px-2.5 py-1 rounded-full border border-[#6C63FF]/20 bg-[#6C63FF]/10 text-[#D7D4FF] text-[11px] font-semibold">
                  {resource.role}
                </span>
              )}
              {resource?.interviewType && (
                <span className="px-2.5 py-1 rounded-full border border-emerald-500/20 bg-emerald-500/10 text-emerald-200 text-[11px] font-semibold capitalize">
                  {resource.interviewType}
                </span>
              )}
              {resource?.youtubeId && (
                <span className="px-2.5 py-1 rounded-full border border-red-500/20 bg-red-500/10 text-red-200 text-[11px] font-semibold">
                  YouTube
                </span>
              )}
            </div>
          )}

          <div className="flex items-center gap-4 text-xs text-white/60 mb-4">
            <div className="flex items-center gap-1.5">
              <Icon name="Clock" size={14} />
              <span>{resource?.duration}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Icon name="Users" size={14} />
              <span>{resource?.completions}</span>
            </div>
            {resource?.questions?.length ? (
              <div className="flex items-center gap-1.5">
                <Icon name="MessageSquareText" size={14} />
                <span>{resource.questions.length} Qs</span>
              </div>
            ) : null}
            {resource?.rating && (
              <div className="flex items-center gap-1.5">
                <Icon name="Star" size={14} className="text-[#8B7CFF]" fill="currentColor" />
                <span>{resource?.rating}</span>
              </div>
            )}
          </div>

          {resource?.progress !== undefined && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-white/55">Progress</span>
                <span className="text-xs text-white/80 font-semibold">{resource?.progress}%</span>
              </div>
              <div className="w-full bg-white/5 rounded-full h-2 overflow-hidden border border-white/10">
                <div
                  className="h-2 rounded-full bg-gradient-to-r from-[#8B7CFF] to-[#6C63FF] transition-all duration-300"
                  style={{ width: `${resource?.progress}%` }}
                />
              </div>
            </div>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={() => onStartPractice && onStartPractice(resource)}
            className="w-full border-white/10 text-white/80 hover:text-white hover:bg-white/5 rounded-2xl"
            iconName="Play"
            iconPosition="left"
          >
            {resource?.progress > 0 ? 'Continue Interview' : 'Start Interview'}
          </Button>
        </div>
      </div>
    </GlareHover>
  );
};

export default ResourceCard;
